import { z } from 'zod';
import { AuditActorSchema } from './audit';
import { ProvenanceBasisSchema } from './enums';
import { AssumptionIdSchema, RunIdSchema } from './ids';
import { EvidenceCitationSchema } from './locator';
import { TimestampSchema } from './primitives';

/**
 * How a fact was established (docs/DECISIONS.md ZD-013, ZD-014).
 *
 * This lives in its own module rather than in `./canonical` because it is no
 * longer a canonical-model concept: **extraction carries the same object**
 * (ZD-014). That is the whole point of the change — an extracted component's
 * provenance is already the shape its canonical counterpart needs, so
 * normalisation lifts it rather than re-deriving it, and there is no step in
 * between where provenance can quietly weaken.
 *
 * `confidence` is deliberately absent at both layers. It is computed from this
 * record by `objectConfidence`, never stored, so no worker — deterministic or
 * LLM — can smuggle a self-reported value underneath the derived one
 * (CLAUDE.md principle 5).
 */
export const ProvenanceSchema = z.object({
  basis: ProvenanceBasisSchema,
  /** Who or what asserted this, and when — distinguishes "the client confirmed
   * this" from "a worker inferred this" after the fact. */
  asserted_by: AuditActorSchema,
  asserted_at: TimestampSchema,
  /**
   * The run that established it, or `null` where no run did
   * (docs/DECISIONS.md ZD-046).
   *
   * **This is what makes a model-asserted fact distinguishable from a
   * deterministic worker's.** `asserted_by` cannot do it: an LLM extractor and
   * a deterministic one both write `{kind: 'worker', id: <name>}`, and they
   * should — the model is a tool the worker used, not an actor, and a fact's
   * actor must not change identity when the worker's implementation does. Run
   * ZD-021's mirror question on the pair and the gap is plain: *where does a
   * worker put the run it was?* Nowhere, until this field.
   *
   * A pointer rather than a copy. The model, model version, temperature, prompt
   * version and pack versions are on the `AgentRun` and in
   * `llm_config_snapshot`; carrying them here as well would be a third record
   * that can disagree with the other two (ZD-017), stored once per fact where
   * they belong once per run. Resolving the pointer answers both questions this
   * exists for — *was this asserted by a model* (`AgentRun.execution_type`) and
   * *which model version said it* (`AgentRun.model`), which is the client-facing
   * form when June's report disagrees with March's.
   *
   * Nullable because a user-asserted fact has no run — ZD-027 rule 4's system
   * name, asserted by the creating user, is the standing instance.
   *
   * **It is not the same axis as `Slot.origin`** (ZD-043 §6), and a future
   * session will be tempted to collapse them. See `statement.ts`.
   */
  asserted_by_run_id: RunIdSchema.nullable(),
  evidence_citations: z.array(EvidenceCitationSchema),
  derived_from_assumption_ids: z.array(AssumptionIdSchema),
});
export type Provenance = z.infer<typeof ProvenanceSchema>;

/**
 * A contradiction needs two sources to contradict each other.
 *
 * The canonical model expresses this with a `Conflict` record whose
 * `competing_assertions` has `.min(2)`. Extraction has no conflict records —
 * assembling those is normalisation's job — so this is the cheapest honest
 * equivalent one layer up: a worker may not mark something `contradicted` while
 * citing a single source, because then nothing disagrees with anything.
 */
export function contradictionIsWitnessed(provenance: Provenance): boolean {
  return provenance.basis !== 'contradicted' || provenance.evidence_citations.length >= 2;
}

/**
 * Whether a fact cites anything at all (docs/DECISIONS.md ZD-015, ZD-106 §1).
 *
 * **ZD-015 was enforced on a citation's *contents* and never on its
 * *existence*.** `evidence_citations` has no `.min(1)`;
 * `EvidenceCitationSchema` requires a locator when the ref names an artefact,
 * and `contradictionIsWitnessed` requires two citations for `contradicted` —
 * but nothing required a `labelled` or `inferred` fact to cite **anything**. So
 * an extractor could invent an object, assert that a document said so, and pass
 * the schema, the integrity check and every layer downstream (ZD-105 §1).
 *
 * No test was wrong to pass and this was not an oversight, it is ZD-055's
 * shape: both producers always cite, so the check had never seen an input that
 * could fail it. **What a check has caught is a fact about the inputs it has
 * seen; what it *can* catch is a property of what the code admits** — and a
 * model is the first producer for which fluent, uncited, plausible assertion is
 * the cheapest available behaviour.
 *
 * ### This is a predicate, and the floor it feeds is an extraction rule
 *
 * `checkExtractionIntegrity` enforces it unconditionally. `checkModelIntegrity`
 * does **not**, and that is measured rather than conceded — see ZD-106 §1. Of
 * the 1,047 provenance records this corpus produces, 585 are extraction and
 * every one cites; 11 canonical records cite nothing and all 11 are correct:
 *
 *  - **8 are ZD-027 rule 4's system name.** A human typed the assessment title,
 *    so there is no document to point at. This is the standing instance of why
 *    `objectConfidence` has a `labelled`-but-uncited → `medium` branch at all.
 *  - **3 are a derived `crosses_boundary_ids` whose value is `[]`.** The flow
 *    crosses nothing, so `boundaryCitations` has no boundary facts to union.
 *
 * Neither is an exception to the floor. Both are **outside** it: a fact
 * established by a person and a fact derived over the model are not claims
 * about what a document says, and the floor is about document-derived facts.
 * Every extraction fact is one by construction, which is why the extraction
 * rule needs no exemption and the canonical layer cannot take the same rule.
 */
export function factIsCited(provenance: Provenance): boolean {
  return provenance.evidence_citations.length >= 1;
}

/**
 * Provenance for the individual attributes of an object
 * (docs/DECISIONS.md ZD-020).
 *
 * ZD-013 said a contradiction blocks **the fact**; `Conflict` carries a single
 * `attribute`; the sds-02a corpus records ground truth per attribute and
 * explains why — *"a basis belongs to a fact: a component can be `labelled` for
 * its name and `inferred` for its type, and a per-object basis averages those
 * into something true of neither."* `Provenance` was nevertheless attached to
 * the object, so a disagreement about one attribute marked the whole object
 * `contradicted` and dragged every finding grounding on it down to `low`.
 *
 * So an object now carries two things:
 *
 *  - `provenance` — how the object's **existence** was established. Why we
 *    believe this component is in the architecture at all.
 *  - `attribute_provenance` — how each named attribute was established, for the
 *    attributes that were established separately.
 *
 * **A missing entry inherits the object's provenance.** Absence means "not
 * separately established", which is the common case and the honest reading: an
 * attribute you cannot separately justify should not be separately claimed.
 * Entries *override*, and their purpose is to record an attribute that is
 * weaker than its object.
 *
 * **The key vocabulary is closed, and that is load-bearing.** A
 * `Record<string, Provenance>` would accept `internet_faceing`, parse happily,
 * be ignored by every reader, and silently fall the real attribute back to the
 * object's provenance — which is exactly the bug ZD-020 exists to remove,
 * reinstated by a spelling mistake and invisible. `z.partialRecord` over a
 * declared enum refuses it at the parse.
 */
export function attributeProvenanceSchema<const T extends readonly [string, ...string[]]>(
  attributes: T,
): z.ZodType<Partial<Record<T[number], Provenance>>> {
  return z.partialRecord(z.enum(attributes), ProvenanceSchema);
}

/**
 * Anything carrying the ZD-020 pair. Declared structurally so `./confidence`
 * can read it without importing a schema, and so both the canonical and the
 * extracted form of an object satisfy the same interface — which is what makes
 * `attributeConfidence` one function rather than two.
 */
export interface Provenanced<A extends string = string> {
  provenance: Provenance;
  attribute_provenance: Partial<Record<A, Provenance>>;
}

/**
 * The provenance governing one attribute: its own entry if it has one, and
 * otherwise the object's.
 *
 * This single function *is* the override-or-inherit rule. Every reader goes
 * through it rather than reaching into the map, so there is one place the rule
 * is written and one place to change it.
 */
export function provenanceOfAttribute<A extends string>(
  object: Provenanced<A>,
  // `NoInfer`, so the vocabulary comes from the object and the attribute is
  // *checked* against it. Inferring from both would let any string widen `A`
  // and quietly restore the typo hole ZD-020 closes.
  attribute: NoInfer<A>,
): Provenance {
  return object.attribute_provenance[attribute] ?? object.provenance;
}

/** Every attribute this object recorded a separate provenance for. */
export function establishedAttributes<A extends string>(object: Provenanced<A>): A[] {
  return Object.keys(object.attribute_provenance) as A[];
}

/**
 * Whether a value is an **absence-marker** — the schema's honest way of saying
 * the source did not say (docs/DECISIONS.md ZD-019).
 *
 * `'unknown'` across `TrustLevel`, `DataClassification`, `Tristate`,
 * `ComponentType` and `TrustBoundaryKind`; `'unestablished'` for
 * `DataSubject` (docs/DECISIONS.md ZD-076 §2, `enums.ts`); `null` for a
 * nullable field; `''` for a description nobody wrote; `[]` for a collection
 * nothing was put in.
 *
 * Three values are deliberately **not** markers:
 *
 *  - `false` is a claim. "This component is not internet-facing" is something a
 *    document states and something a pack pattern grounds on, and treating it as
 *    absence would discard the distinction the whole `Tristate` exists for.
 *  - `'other'` is a claim (ZD-019). It means the document stated a type this
 *    enum cannot hold — a load balancer, a scheduled job — which is why
 *    `unknown` had to be added separately.
 *  - `'not_applicable'` on `DataSubject` is a claim for the same reason `false`
 *    is: "this asset is not personal data about anyone" is a citable statement
 *    about one asset, not a report that nothing was said. Folding it into
 *    absence would let it inherit a citation for a different attribute rather
 *    than carry its own — exactly the trapdoor this function's own
 *    `attributesMissingProvenance` counterpart exists to close. It has no
 *    producer in this corpus yet (`enums.ts`), so the question is unforced for
 *    now rather than answered by omission.
 */
export function isAbsenceMarker(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (value === 'unknown' || value === 'unestablished' || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Justifies every attribute asserting a real value with one provenance record.
 *
 * Legitimate exactly when one citation really does cover the whole object — a
 * structured description where every attribute of a component sits at the same
 * JSON pointer, or a questionnaire answer. It is **not** a way round
 * `attributesMissingProvenance`: the point of that rule is that the claim be
 * *stated* rather than inherited, and a worker calling this is stating it.
 *
 * A worker whose evidence differs attribute by attribute — anything reading
 * prose or a diagram — cannot use this honestly, and passes `overrides`, or
 * builds the map itself. That is the whole difference between the deterministic
 * extractor and the multimodal one, and it is now visible in the code rather
 * than left to a reviewer's memory.
 */
export function attributeProvenanceFrom<A extends string>(
  values: object,
  attributes: readonly A[],
  provenance: Provenance,
  overrides: Partial<Record<A, Provenance>> = {},
): Partial<Record<A, Provenance>> {
  const record = values as Record<string, unknown>;
  const justified: Partial<Record<A, Provenance>> = {};
  for (const attribute of attributes) {
    if (isAbsenceMarker(record[attribute])) continue;
    justified[attribute] = overrides[attribute] ?? provenance;
  }
  return { ...justified, ...overrides };
}

/**
 * Attributes that assert a real value and do not carry their own provenance
 * (docs/DECISIONS.md ZD-020).
 *
 * **This is the constraint that makes inheritance safe.** Inheritance exists so
 * that an attribute nobody established does not have to invent a basis — but on
 * its own it is a trapdoor back to per-object provenance: a component whose
 * *existence* is `labelled` with a citation would lend that `labelled` to
 * `internet_facing: true` even where no source states it, and
 * `attributeConfidence` would return `high` for a claim nothing supports.
 *
 * So inheritance may only ever **weaken**, never strengthen: an attribute
 * holding an absence-marker inherits (it asserts nothing, so there is nothing
 * to justify), and an attribute asserting a real value must carry its own
 * record. `checkModelIntegrity` and `checkExtractionIntegrity` both enforce it,
 * so an unjustified claim is refused rather than quietly credited to the
 * object.
 *
 * A *derived* empty value is the one case this cannot see — a
 * `crosses_boundary_ids: []` that normalisation worked out asserts "this flow
 * crosses nothing", and is identical in shape to the `[]` left behind when
 * nothing ever derived it. That distinction is not decidable from the value, so
 * it is not decided here: `checkModelIntegrity` asks whether the *model* placed
 * the flow, and requires the record only when it did (docs/DECISIONS.md ZD-027).
 */
export function attributesMissingProvenance<A extends string>(
  object: Provenanced<A>,
  attributes: readonly A[],
): A[] {
  const values = object as unknown as Record<string, unknown>;
  return attributes.filter(
    (attribute) =>
      !isAbsenceMarker(values[attribute]) && object.attribute_provenance[attribute] === undefined,
  );
}

/**
 * Empty collections that carry a provenance record of their own
 * (docs/DECISIONS.md ZD-036).
 *
 * `attributesMissingProvenance` is the *requirement* — a stated value must
 * justify itself. This is the mirror **prohibition**, and it exists because
 * ZD-020 left permitting the reverse open: nothing refused a worker writing
 * `resides_in: []` *with* a record, which is how an extractor says "established
 * as holding nothing" rather than "nobody worked it out".
 *
 * That distinction is real and this refuses it deliberately, for two reasons
 * that hold independently:
 *
 *  - **It is a denial, and a denial is the dangerous direction.** ZD-029 §3
 *    reads a collection's *establishment* to decide `unknown` against
 *    `true`/`false`, so one extra key turns "nobody said where this personal
 *    data lives" into "it lives nowhere" — and `where(carried_asset, some, …)`
 *    into `false`, suppressing a finding. That is CLAUDE.md principle 9's
 *    primary failure mode, reachable by a model writing a map entry.
 *  - **The layer below cannot carry it anyway.** Normalisation's `factsOf`
 *    filters on `isStated`, and `[]` is not stated, so an established-empty
 *    extraction fact is silently dropped at the merge. Permitting a value the
 *    next layer discards is worse than refusing it: it reads as recorded.
 *
 * **Collections only, and scalars deliberately not.** A scalar absence-marker
 * (`encrypted: 'unknown'`) is decidable under ZD-029 whether or not it is
 * established — the rule only ever bites on empties, which is exactly where the
 * ambiguity lives. Widening this to every marker would refuse a record nothing
 * misreads.
 *
 * **Extraction only.** The canonical model legitimately holds one derived
 * established-empty — `crosses_boundary_ids`, governed by ZD-027 rule 2's
 * derivability test — and normalisation is its only writer.
 */
export function emptyCollectionsClaimingProvenance<A extends string>(
  object: Provenanced<A>,
  attributes: readonly A[],
): A[] {
  const values = object as unknown as Record<string, unknown>;
  return attributes.filter((attribute) => {
    const value = values[attribute];
    return (
      Array.isArray(value) &&
      value.length === 0 &&
      object.attribute_provenance[attribute] !== undefined
    );
  });
}
